import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore, type OperatorStatus } from '../store/auth';
import { setMyStatus } from '../api/auth';
import { getApiClient } from '../api/factory';
import { useT, type DictKey } from '../i18n';
import { StatusDot } from './StatusDot';

// ANX-071 — переключатель статуса присутствия оператора (online/away/busy)
// в подвале сайдбара. Меняет статус через PATCH /me/status, обновляет
// локальную сессию и инвалидирует список операторов, чтобы коллеги увидели
// смену при ближайшем refetch'е (realtime-broadcast — задел для ANX-069).

const OPTIONS: readonly OperatorStatus[] = ['online', 'away', 'busy'];
const LABEL_KEY: Record<OperatorStatus, DictKey> = {
  online: 'status.online',
  away: 'status.away',
  busy: 'status.busy',
};

export function StatusToggle() {
  const session = useAuthStore((s) => s.session);
  const setSession = useAuthStore((s) => s.setSession);
  const t = useT();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);

  if (!session) return null;
  const current: OperatorStatus = session.operator.presenceStatus ?? 'online';

  async function select(status: OperatorStatus) {
    if (pending || !session) return;
    setOpen(false);
    if (status === current) return;
    setPending(true);
    try {
      await setMyStatus(getApiClient(), status);
      setSession({ ...session, operator: { ...session.operator, presenceStatus: status } });
      void queryClient.invalidateQueries({ queryKey: ['operators'] });
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={t('status.label')}
        onClick={() => setOpen((v) => !v)}
        disabled={pending}
        className="inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-xs text-slate-500 hover:text-slate-900 disabled:opacity-50"
      >
        <StatusDot status={current} />
        {t(LABEL_KEY[current])}
      </button>
      {open && (
        <div className="absolute bottom-full left-0 z-10 mb-1 w-36 rounded-md border border-slate-200 bg-white py-1 text-xs shadow-pop">
          {OPTIONS.map((s) => (
            <button
              key={s}
              type="button"
              disabled={pending}
              onClick={() => void select(s)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-50 disabled:opacity-50"
            >
              <StatusDot status={s} />
              {t(LABEL_KEY[s])}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
